/**
 * @module components/modals/Pdf3dExportModal
 * @summary The export dialog for the 3D view portfolio.
 * @description
 *   A sibling of `PdfExportModal`, not a mode of it. The two dialogs share their
 *   chrome and almost nothing else, because the two documents answer different
 *   questions — see the table in `export3dPdf`. Folding them together would mean
 *   a plot-scale control that is meaningless here and a view picker that is
 *   meaningless there, both greyed out half the time.
 *
 *   What this one asks is: which views, how big, and does the tag list travel
 *   with them. What it does not ask is the plot scale, because an isometric does
 *   not have one — every sheet it makes is stamped NTS.
 */
import { useEffect, useState } from 'react';
import { engine } from '../../engine/CanvasEngine';
import { engine3d, type CaptureSpec } from '../../engine3d/Engine3D';
import { export3dPdf, plan3dExport, type Pdf3dOptions, type ViewLayout } from '../../engine/export/export3dPdf';
import { PAPERS, type PaperName } from '../../engine/export/pdf/sheet';
import { lsGet, lsSet } from '../../lib/storage';

interface Props {
  open: boolean;
  onClose: () => void;
}

const PREFS_KEY = 'jcc_pdf3d_export_v1';

/**
 * The views on offer, keyed so a remembered selection survives a reorder.
 *
 * `current` comes first and is on by default: it is the only one somebody
 * actually composed, and it is usually the whole reason they pressed the button.
 * The rest are the standard set an approval drawing carries.
 */
const VIEWS: { key: string; spec: CaptureSpec; hint: string }[] = [
  { key: 'current', spec: { preset: 'current', label: 'As viewed' }, hint: 'your current camera' },
  { key: 'iso', spec: { preset: 'iso', label: 'Isometric — south-west' }, hint: '' },
  { key: 'isoNE', spec: { preset: 'isoNE', label: 'Isometric — north-east' }, hint: '' },
  { key: 'top', spec: { preset: 'top', label: 'Plan' }, hint: '' },
  { key: 'front', spec: { preset: 'front', label: 'Elevation — south' }, hint: '' },
  { key: 'side', spec: { preset: 'side', label: 'Elevation — east' }, hint: '' },
];

const STATUSES = [
  'ISSUED FOR APPROVAL',
  'ISSUED FOR QUOTATION',
  'ISSUED FOR TENDER',
  'FOR INFORMATION ONLY',
  'PRELIMINARY',
  'AS BUILT',
  '',
] as const;

interface Prefs {
  paper: PaperName;
  orientation: 'landscape' | 'portrait';
  layout: ViewLayout;
  /** Selected view keys, in `VIEWS` order. */
  views: string[];
  status: string;
  watermark: boolean;
  includeSchedule: boolean;
  scheduleCsvToo: boolean;
}

const DEFAULT_PREFS: Prefs = {
  paper: 'A3',
  orientation: 'landscape',
  layout: 'grid',
  views: ['current', 'iso', 'top', 'front'],
  status: 'ISSUED FOR APPROVAL',
  watermark: false,
  includeSchedule: false,
  scheduleCsvToo: false,
};

/** The selected specs, always in the canonical order rather than click order. */
function chosen(p: Prefs): { key: string; spec: CaptureSpec }[] {
  return VIEWS.filter((v) => p.views.includes(v.key));
}

function toOptions(p: Prefs): Pdf3dOptions {
  return {
    paper: p.paper,
    orientation: p.orientation,
    layout: p.layout,
    status: p.status,
    watermark: p.watermark ? p.status || 'PRELIMINARY' : undefined,
    includeSchedule: p.includeSchedule,
    scheduleCsvToo: p.scheduleCsvToo,
    viewCount: chosen(p).length,
  };
}

export function Pdf3dExportModal({ open, onClose }: Props) {
  const [p, setP] = useState<Prefs>(() => ({ ...DEFAULT_PREFS, ...lsGet(PREFS_KEY, {}) }));
  const set = <K extends keyof Prefs>(k: K, v: Prefs[K]) => setP((q) => ({ ...q, [k]: v }));
  /** Set while the renderer is working through the views. */
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) lsSet(PREFS_KEY, p);
  }, [p, open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose, busy]);

  if (!open) return null;

  const picked = chosen(p);
  const plan = plan3dExport(toOptions(p));
  const empty = engine3d.getStats().shapes === 0;

  const toggleView = (key: string) =>
    set(
      'views',
      p.views.includes(key) ? p.views.filter((k) => k !== key) : [...p.views, key],
    );

  const go = () => {
    if (!picked.length) return;
    setBusy(true);
    // A yield before the work: rendering six views through a software
    // rasteriser blocks the main thread for a moment, and without this the
    // button never gets to paint its "Rendering…" state.
    setTimeout(() => {
      try {
        // Each view is rendered at the shape of the frame it will sit in, so a
        // wide plant on a wide sheet is not letterboxed into a strip.
        const wide = p.layout === 'full';
        const views = engine3d.captureViews(
          picked.map((v) => v.spec),
          wide ? 720 : 520,
          wide ? 460 : 360,
          wide ? 3 : 4,
        );
        void export3dPdf({ ...toOptions(p), views });
      } finally {
        setBusy(false);
        onClose();
      }
    }, 30);
  };

  return (
    <div className="modal-overlay" onClick={() => !busy && onClose()}>
      <div className="pdfx" onClick={(e) => e.stopPropagation()}>
        <div className="pdfx-head">
          <div>
            <div className="pdfx-title">Export 3D views</div>
            <div className="pdfx-sub">
              A view portfolio — not to scale. For the measurable drawing, export from 2D.
            </div>
          </div>
          <button className="pdfx-x" title="Close" onClick={onClose} disabled={busy}>
            ✕
          </button>
        </div>

        {/* ── What you are about to get ── */}
        <div className={'pdfx-plan' + (empty || !picked.length ? ' warn' : '')}>
          {empty ? (
            <span>Nothing in the 3D view to export.</span>
          ) : !plan ? (
            <span>Pick at least one view.</span>
          ) : (
            <>
              <span className="pdfx-plan-big">
                {plan.sheets}
                <span style={{ fontSize: 13, fontWeight: 600 }}>
                  {' '}
                  sheet{plan.sheets > 1 ? 's' : ''}
                </span>
              </span>
              <span className="pdfx-plan-rest">
                {plan.views} view{plan.views > 1 ? 's' : ''} on {plan.paper}{' '}
                {plan.orientation === 'landscape' ? 'landscape' : 'portrait'}
                {plan.layout === 'full' ? ', one per sheet' : ', four to a sheet'}
                {plan.scheduleSheets > 0 && (
                  <> · {plan.scheduleRows} scheduled items over {plan.scheduleSheets} more</>
                )}
                <br />
                Every sheet is stamped <strong>NTS</strong> — a 3D view has no plot scale to
                state, and stating one would invite a reader to measure it.
              </span>
            </>
          )}
        </div>

        {/* ── Which views ── */}
        <label className="pdfx-lbl">
          Views
          <span className="pdfx-hint"> rendered off screen; your camera is left as it is</span>
        </label>
        <div className="pdfx-chips">
          {VIEWS.map((v) => (
            <button
              key={v.key}
              className={p.views.includes(v.key) ? 'on' : ''}
              onClick={() => toggleView(v.key)}
              title={v.hint || v.spec.label}
            >
              {v.spec.label.replace(' — ', ' · ')}
            </button>
          ))}
        </div>

        <label className="pdfx-lbl">Arrangement</label>
        <div className="pdfx-seg">
          <button className={p.layout === 'grid' ? 'on' : ''} onClick={() => set('layout', 'grid')}>
            Four to a sheet
          </button>
          <button className={p.layout === 'full' ? 'on' : ''} onClick={() => set('layout', 'full')}>
            One per sheet
          </button>
        </div>

        <label className="pdfx-lbl">Sheet size</label>
        <div className="pdfx-seg">
          {(Object.keys(PAPERS) as PaperName[]).map((n) => (
            <button key={n} className={p.paper === n ? 'on' : ''} onClick={() => set('paper', n)}>
              {n}
            </button>
          ))}
        </div>

        <label className="pdfx-lbl">Orientation</label>
        <div className="pdfx-seg">
          <button
            className={p.orientation === 'landscape' ? 'on' : ''}
            onClick={() => set('orientation', 'landscape')}
          >
            Landscape
          </button>
          <button
            className={p.orientation === 'portrait' ? 'on' : ''}
            onClick={() => set('orientation', 'portrait')}
          >
            Portrait
          </button>
        </div>

        <label className="pdfx-lbl">Issue status</label>
        <select value={p.status} onChange={(e) => set('status', e.target.value)}>
          {STATUSES.map((s) => (
            <option key={s || 'none'} value={s}>
              {s || '— none —'}
            </option>
          ))}
        </select>

        <label className="pdfx-chk">
          <input
            type="checkbox"
            checked={p.watermark}
            onChange={(e) => set('watermark', e.target.checked)}
          />
          <span>
            Overprint the status across the sheet
            <em> — for a view nobody may build from yet</em>
          </span>
        </label>

        <label className="pdfx-chk">
          <input
            type="checkbox"
            checked={p.includeSchedule}
            onChange={(e) => set('includeSchedule', e.target.checked)}
          />
          <span>
            Equipment schedule sheet
            <em> — so a reader can look up the tag on a vessel they can see</em>
          </span>
        </label>

        <label className="pdfx-chk">
          <input
            type="checkbox"
            checked={p.scheduleCsvToo}
            disabled={!p.includeSchedule}
            onChange={(e) => set('scheduleCsvToo', e.target.checked)}
          />
          <span>
            Also download the schedule as CSV
            <em> — for a supplier who would rather have a spreadsheet</em>
          </span>
        </label>

        <div className="pdfx-note">
          The 3D view is drawn by a shading renderer, so these sheets carry images
          rather than lines. Labels and floors follow the toggles you have set in the
          3D view — {engine3d.isMounted() ? 'what you see is what prints' : 'open the 3D view first'}.
          Document: <strong>{engine.getDocMeta().docName || 'Untitled'}</strong>.
        </div>

        <div className="pdfx-btns">
          <button className="pdfx-btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className="pdfx-btn primary"
            disabled={busy || empty || !picked.length}
            onClick={go}
          >
            {busy ? 'Rendering…' : `Export ${plan ? plan.sheets : 0} sheet${plan && plan.sheets > 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}
